import { useMemo, useState } from 'react';
import {
  kr,
  purchasingPower,
  gaeldsfaktor,
  financingSplit,
  belaaningsgrad,
  bidragssats,
  annuityPayment,
  totalInterest,
  amortizationSchedule,
  raadighedsbeloeb,
  oneTimeCosts,
  purchaseScenario,
  type PropertyType,
} from '@heivol-finance/calc';

/**
 * View-model hook for the LoanCalculator route (extracted from LoanCalculator.tsx).
 * All inputs are WHOLE KRONER (this screen's state is kroner); the derived figures are
 * øre via the calc engine. Purely mechanical state + derivation — the math itself is
 * the calc package's (unit-tested there).
 */
export function useLoanCalculator() {
  // Borrowing capacity
  const [grossMonthly, setGrossMonthly] = useState(55000);
  const [existingDebt, setExistingDebt] = useState(0);
  const [maxFactor, setMaxFactor] = useState(4);
  const [price, setPrice] = useState(3000000);
  const [type, setType] = useState<PropertyType>('ejerbolig');
  const [ratePct, setRatePct] = useState(4);
  const [termYears, setTermYears] = useState(30);

  // Rådighedsbeløb
  const [netMonthly, setNetMonthly] = useState(38000);
  const [fixedMonthly, setFixedMonthly] = useState(22000);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(1);

  const grossAnnual = kr(grossMonthly) * 12;
  const kobekraft = purchasingPower(grossAnnual, kr(existingDebt), maxFactor);
  const split = financingSplit(kr(price), type);
  const totalDebtAtPrice = split.realkredit + split.bankloan + kr(existingDebt);
  const resultingFactor = gaeldsfaktor(totalDebtAtPrice, grossAnnual);
  const ltv = belaaningsgrad(split.realkredit, kr(price));

  // Monthly payment breakdown (30-yr fixed annuity stress test on the total secured debt).
  const payment = useMemo(() => {
    const secured = split.realkredit + split.bankloan;
    const r = ratePct / 100 / 12;
    const n = termYears * 12;
    const monthly = annuityPayment(secured, r, n);
    const firstInterest = Math.round(secured * r);
    const firstPrincipal = monthly - firstInterest;
    const bidragMonthly = Math.round((split.realkredit * bidragssats(ltv)) / 12);
    const schedule = amortizationSchedule(secured, r, n);
    return {
      secured,
      monthly,
      firstInterest,
      firstPrincipal,
      bidragMonthly,
      totalWithBidrag: monthly + bidragMonthly,
      lifetimeInterest: totalInterest(schedule),
    };
  }, [split.realkredit, split.bankloan, ratePct, termYears, ltv]);

  const raad = raadighedsbeloeb({
    netMonthlyIncome: kr(netMonthly),
    fixedMonthlyExpenses: kr(fixedMonthly) + payment.totalWithBidrag,
    adults,
    children,
  });

  const costs = oneTimeCosts({ price: kr(price), mortgage: split.realkredit });

  // Two named scenarios (sheet: buy+teardown+rebuild−savings vs buy+renovate−savings).
  const [savings, setSavings] = useState(800000);
  const scenarioA = purchaseScenario({
    name: 'Buy + teardown + rebuild',
    costs: [kr(price), kr(200000), kr(3180000)],
    savings: kr(savings),
  });
  const scenarioB = purchaseScenario({
    name: 'Buy + renovate',
    costs: [kr(price), kr(900000)],
    savings: kr(savings),
  });

  return {
    // Inputs
    grossMonthly, setGrossMonthly,
    existingDebt, setExistingDebt,
    maxFactor, setMaxFactor,
    price, setPrice,
    type, setType,
    ratePct, setRatePct,
    termYears, setTermYears,
    netMonthly, setNetMonthly,
    fixedMonthly, setFixedMonthly,
    adults, setAdults,
    children, setChildren,
    savings, setSavings,
    // Derived
    grossAnnual,
    kobekraft,
    split,
    resultingFactor,
    ltv,
    payment,
    raad,
    costs,
    scenarioA,
    scenarioB,
  };
}
